import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Draggable from 'react-draggable';

const PUZZLE_ROWS = 3;
const PUZZLE_COLS = 3;
const SNAP_TOLERANCE = 20;

// Fireworks component for piece placement
const PieceFireworks = ({ show }) => {
  if (!show) return null;

  return (
    <div className="piece-fireworks">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="particle" style={{
          '--angle': `${i * 45}deg`,
          '--delay': `${i * 50}ms`,
        }} />
      ))}
    </div>
  );
};

// Grand fireworks component for puzzle completion
const CompletionFireworks = () => {
  return (
    <div className="completion-fireworks">
      {[...Array(30)].map((_, i) => (
        <div
          key={i}
          className="firework"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 2}s`,
            '--hue': `${Math.floor(Math.random() * 360)}deg`,
          }}
        >
          {[...Array(8)].map((_, j) => (
            <div
              key={j}
              className="spark"
              style={{
                '--angle': `${j * 45}deg`,
                '--distance': `${20 + Math.random() * 30}px`,
                '--duration': `${0.5 + Math.random()}s`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

function shuffleArray(array) {
  return array
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
}

function Puzzle({ imageSrc }) {
  const [pieces, setPieces] = useState([]);
  const [positions, setPositions] = useState({});
  const [solved, setSolved] = useState(false);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageKey, setImageKey] = useState(Date.now());
  const [activePiece, setActivePiece] = useState(null);
  const [justPlacedPiece, setJustPlacedPiece] = useState(null);
  const [showCompletionFireworks, setShowCompletionFireworks] = useState(false);

  useEffect(() => {
    if (!imageSrc) return;

    // Reset solved state and create a new image key when image changes
    setSolved(false);
    setShowCompletionFireworks(false);
    setImageKey(Date.now());

    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });

      const newPieces = [];
      const newPositions = {};

      for (let row = 0; row < PUZZLE_ROWS; row++) {
        for (let col = 0; col < PUZZLE_COLS; col++) {
          const id = row * PUZZLE_COLS + col;
          newPieces.push({
            id,
            row,
            col,
            ref: React.createRef(),
          });

          // Initial positions will be set after container size is known
          newPositions[id] = {
            x: 0,
            y: 0,
            placed: false,
          };
        }
      }

      const shuffled = shuffleArray(newPieces);
      setPieces(shuffled);
      setPositions(newPositions);

      // Immediately try to distribute pieces if container size is already known
      if (containerRef.current && containerSize.width > 0 && containerSize.height > 0) {
        randomizePiecePositions(newPieces, newPositions);
      }
    };
  }, [imageSrc]);

  // Function to randomize piece positions
  const randomizePiecePositions = (piecesToRandomize, currentPositions) => {
    if (!containerRef.current || containerSize.width === 0 || containerSize.height === 0) return;

    const pieceWidth = containerSize.width / PUZZLE_COLS;
    const pieceHeight = containerSize.height / PUZZLE_ROWS;

    const newPositions = { ...currentPositions };

    piecesToRandomize.forEach(piece => {
      // Randomize all positions when setting up a new puzzle
      newPositions[piece.id] = {
        x: Math.random() * (containerSize.width - pieceWidth),
        y: Math.random() * (containerSize.height - pieceHeight),
        placed: false
      };
    });

    setPositions(newPositions);
  };

  useEffect(() => {
    if (containerRef.current) {
      const updateSize = () => {
        const { offsetWidth, offsetHeight } = containerRef.current;
        setContainerSize({ width: offsetWidth, height: offsetHeight });
      };

      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }
  }, [containerRef.current]);

  // Separate effect to handle distribution of pieces after container size is known
  useEffect(() => {
    if (pieces.length > 0 && containerSize.width > 0 && containerSize.height > 0) {
      randomizePiecePositions(pieces, positions);
    }
  }, [containerSize, imageKey]); // Only run when container size changes or image changes

  const handleStart = (piece) => {
    setActivePiece(piece.id);
  };

  const handleStop = (e, data, piece) => {
    setActivePiece(null);

    if (containerSize.width === 0 || containerSize.height === 0) return;

    const pieceWidth = containerSize.width / PUZZLE_COLS;
    const pieceHeight = containerSize.height / PUZZLE_ROWS;
    const targetX = piece.col * pieceWidth;
    const targetY = piece.row * pieceHeight;

    const deltaX = Math.abs(data.x - targetX);
    const deltaY = Math.abs(data.y - targetY);

    const newPos = { ...positions };
    const wasAlreadyPlaced = newPos[piece.id]?.placed || false;

    if (deltaX < SNAP_TOLERANCE && deltaY < SNAP_TOLERANCE) {
      // Snap to correct position
      newPos[piece.id] = {
        x: targetX,
        y: targetY,
        placed: true
      };

      // Only show fireworks if this piece wasn't already placed
      if (!wasAlreadyPlaced) {
        setJustPlacedPiece(piece.id);
        setTimeout(() => setJustPlacedPiece(null), 800); // Remove fireworks after animation completes
      }
    } else {
      // Keep current position
      newPos[piece.id] = {
        ...newPos[piece.id],
        x: data.x,
        y: data.y,
        placed: false
      };
    }

    setPositions(newPos);

    // Check if all pieces are in correct positions
    const allCorrect = pieces.every((p) => {
      const pos = newPos[p.id];
      return pos && pos.placed;
    });

    if (allCorrect && !solved) {
      setSolved(true);
      setShowCompletionFireworks(true);
      // Add a little delay before showing completion message
      setTimeout(() => {
        setShowCompletionFireworks(false);
      }, 4000);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-[800px] mx-auto aspect-video border-2 rounded-xl overflow-hidden bg-gray-200 shadow-lg"
      style={{ minHeight: '300px', height: 'calc(100vh - 400px)', maxHeight: '600px' }}
    >
      {pieces.map((piece) => (
        <Draggable
          key={`${piece.id}-${imageKey}`}
          bounds="parent"
          nodeRef={piece.ref}
          position={positions[piece.id] ? { x: positions[piece.id].x, y: positions[piece.id].y } : { x: 0, y: 0 }}
          onStart={() => handleStart(piece)}
          onStop={(e, data) => handleStop(e, data, piece)}
        >
          <div
            ref={piece.ref}
            className="absolute touch-manipulation"
            style={{
              width: containerSize.width > 0 ? `${100 / PUZZLE_COLS}%` : '33.333%',
              height: containerSize.height > 0 ? `${100 / PUZZLE_ROWS}%` : '33.333%',
              zIndex: activePiece === piece.id ? 100 : positions[piece.id]?.placed ? 5 : 10,
            }}
          >
            <div
              className="w-full h-full cursor-move"
              style={{
                backgroundImage: `url(${imageSrc})`,
                backgroundSize: `${PUZZLE_COLS * 100}% ${PUZZLE_ROWS * 100}%`,
                backgroundPosition: `${-piece.col * 100}% ${-piece.row * 100}%`,
                border: '2px solid white',
                boxShadow: activePiece === piece.id
                  ? '0 8px 16px rgba(0,0,0,0.2), 0 0 0 2px #3b82f6'
                  : positions[piece.id]?.placed
                    ? '0 2px 3px rgba(0,0,0,0.05)'
                    : '0 4px 6px rgba(0,0,0,0.1)',
                borderRadius: '4px',
                transform: activePiece === piece.id ? 'scale(1.02)' : 'scale(1)',
                transition: 'box-shadow 0.2s, transform 0.2s',
                opacity: positions[piece.id]?.placed ? 0.95 : 1,
              }}
            ></div>
            {justPlacedPiece === piece.id && (
              <PieceFireworks show={true} />
            )}

          </div>
        </Draggable>
      ))}

      {solved && (
        <div className="absolute inset-0 bg-green-500 bg-opacity-50 flex items-center justify-center text-white text-2xl font-bold z-[200]">
          Puzzle Solved!
          {showCompletionFireworks && <CompletionFireworks />}
        </div>
      )}
    </div>
  );
}

export default function JigsawPuzzleGenerator() {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [readymadeImages] = useState([
    'https://images.bauerhosting.com/legacy/empire-tmdb/films/8587/images/klI0K4oQMsLhHdjA9Uw8WLugk9v.jpg?ar=16%3A9&fit=crop&crop=top&auto=format&w=1440&q=80',
    'https://images4.alphacoders.com/120/thumb-1920-1205150.jpg',
    'https://rukminim3.flixcart.com/image/850/1000/ke0a7ww0-0/poster/v/a/2/medium-sa-1905-masha-and-the-bear-cartoon-sticker-poster-cartoon-original-imafuse43sakyy4y.jpeg?q=90&crop=false',
    'https://store-images.s-microsoft.com/image/apps.39852.14455250034361967.824623c3-91b6-402f-9a68-c66bfac9bc20.7bfae929-5b2a-4efe-9639-5ffd1e2de297?q=90&w=480&h=270',
    'https://imgix.ranker.com/list_img_v2/19530/3299530/original/3299530',
    'https://assets.teenvogue.com/photos/5beb340b368e21796e2d8b85/16:9/w_2560%2Cc_limit/ppg2016_keyart_horizontal_nologos_final.jpg',
    'https://i.ytimg.com/vi/C_KZ2TxiOpA/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLC1KdNOkXW2ziZkQ2mrOkr_fEslkA',
    'https://variety.com/wp-content/uploads/2023/05/spider-2.jpg?w=1000&h=563&crop=1',
    'https://www.thedigitalfix.com/wp-content/sites/thedigitalfix/2023/10/best-harry-potter-characters.jpg'
  ]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
        setImageUrl(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReadymadeClick = (url) => {
    setUploadedImage(null);
    setImageUrl(url);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-center">Jigsaw Puzzle Generator</h1>

        <div className="mb-4 md:mb-6 bg-white rounded-xl shadow p-3 md:p-4">
          <h2 className="text-lg md:text-xl font-semibold mb-2">Upload Your Image</h2>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        <div className="mb-4 md:mb-6 bg-white rounded-xl shadow p-3 md:p-4">
          <h2 className="text-lg md:text-xl font-semibold mb-2">Or Select a Readymade Puzzle</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-4">
            {readymadeImages.map((img, index) => (
              <motion.img
                key={index}
                src={img}
                alt={`Puzzle ${index + 1}`}
                className="rounded-xl cursor-pointer shadow-md hover:scale-105 transition-transform duration-300"
                onClick={() => handleReadymadeClick(img)}
                whileHover={{ scale: 1.05 }}
              />
            ))}
          </div>
        </div>

        {(uploadedImage || imageUrl) && (
          <div className="bg-white rounded-xl shadow p-3 md:p-4">
            <h2 className="text-lg md:text-xl font-semibold mb-2">Puzzle Preview</h2>
            <Puzzle imageSrc={uploadedImage || imageUrl} />
            <p className="text-center mt-3 text-sm text-gray-600">Drag pieces to solve the puzzle!</p>
          </div>
        )}
      </div>

      <style jsx global>{`
        /* Piece fireworks animation */
        .piece-fireworks {
          position: absolute;
          inset: -20px;
          z-index: 50;
          pointer-events: none;
        }

        .piece-fireworks .particle {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: linear-gradient(180deg, #ffde59, #ff914d);
          transform-origin: center;
          transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-20px);
          animation: pieceParticle 0.6s ease-out forwards;
          animation-delay: var(--delay);
          opacity: 0;
        }

        @keyframes pieceParticle {
          0% {
            opacity: 1;
            transform: translate(-50%, -50%) rotate(var(--angle)) translateY(0);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-40px);
          }
        }

        /* Completion fireworks animation */
        .completion-fireworks {
          position: absolute;
          inset: 0;
          z-index: 999;
          pointer-events: none;
          overflow: hidden;
        }

        .firework {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: explode 2s forwards;
        }

        .spark {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: hsl(var(--hue), 100%, 70%);
          box-shadow: 0 0 8px 1px hsl(var(--hue), 100%, 80%);
          transform: translate(-50%, -50%);
          animation: spark var(--duration) forwards ease-out;
          opacity: 0;
        }

        @keyframes explode {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }

        @keyframes spark {
          0% {
            opacity: 1;
            transform: translate(-50%, -50%) rotate(var(--angle)) translateY(0);
          }
          70% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) rotate(var(--angle)) translateY(var(--distance));
          }
        }
      `}</style>
    </div>
  );
}